import { epley, isE1rmRepCount } from "./pr.js";
import { weightedVolumeKg } from "./setVolume.js";
import type { Workout, WorkoutSet } from "./schemas/workout.js";

// Per-exercise progression series consumed by the Progress surfaces on
// both web and mobile. Lives in shared so the two surfaces compute the
// same numbers without diverging. epley() comes from pr.ts.

export type Point = {
  /** The instant this datum happened, epoch ms. Renderers MUST place it on a
   *  real time axis: a series that stops must LOOK stopped. (HM-1 — this said
   *  "renderers draw equal-spaced", and they did: LineChart took x from the
   *  array index and ignored this field entirely, so 25 days of readings plus
   *  a 5-day hole drew identically to 30 full days. That is how the CGM
   *  pipeline's death went unnoticed for five days.) Ascending. */
  x: number;
  y: number;
  label: string;
};

export type Series = {
  exerciseId: string;
  exerciseName: string;
  e1rm: Point[];
  volume: Point[];
};

export type BuildSeriesOptions = {
  // Locale-aware date formatter for chart tooltips. Defaults to "medium"
  // in the runtime's default locale — fine for the chart's title hover,
  // and callers pass their own formatter to follow user-i18n.
  dateFormatter?: Intl.DateTimeFormat;
};

export const buildSeries = (
  workouts: Workout[],
  opts: BuildSeriesOptions = {},
): Series[] => {
  // Sort sessions oldest first so the chart reads left-to-right.
  const sorted = [...workouts].sort(
    (a, b) =>
      new Date(a.startedAt).getTime() - new Date(b.startedAt).getTime(),
  );
  const dateFmt =
    opts.dateFormatter ??
    new Intl.DateTimeFormat(undefined, { dateStyle: "medium" });
  const groups = new Map<string, Series>();

  for (const w of sorted) {
    for (const entry of w.entries) {
      // A fail counts like any set (AP-0p model): `reps` is the clean reps
      // done and `fail` is a pure label, so failed weighted sets are NOT
      // filtered out of e1RM / volume.
      const weighted = entry.sets.filter(
        (s): s is Extract<WorkoutSet, { type: "weighted" }> =>
          s.type === "weighted",
      );
      if (weighted.length === 0) continue;
      // e1RM gated by E1RM_REP_CAP — past 12 reps Epley diverges so we
      // drop the point rather than plot inflated numbers. Volume math
      // is independent (no cap) so the volume series records every
      // weighted set regardless of rep count.
      const e1rmEligible = weighted.filter((s) => isE1rmRepCount(s.reps));
      const topE1RM = e1rmEligible.reduce(
        (acc, s) => Math.max(acc, epley(s.weight, s.reps)),
        0,
      );
      const volume = weighted.reduce(
        (acc, s) => acc + weightedVolumeKg(s.weight, s.reps, s.loadedSides),
        0,
      );
      const dateLabel = dateFmt.format(new Date(w.startedAt));
      const key = entry.exerciseId;
      let series = groups.get(key);
      if (!series) {
        series = {
          exerciseId: key,
          exerciseName: entry.exerciseName ?? entry.exerciseId,
          e1rm: [],
          volume: [],
        };
        groups.set(key, series);
      }
      // Latest exerciseName wins so a coach rename surfaces immediately
      // even though historical entries keep their stamped value.
      series.exerciseName = entry.exerciseName ?? series.exerciseName;
      // x index for volume points (every weighted-set day gets one).
      // e1RM points use their own index so a session of all 13+ rep
      // sets just doesn't push an e1RM point — no phantom 0.
      const volIndex = series.volume.length;
      if (topE1RM > 0) {
        const roundedE1RM = Math.round(topE1RM * 10) / 10;
        const eIndex = series.e1rm.length;
        series.e1rm.push({
          x: eIndex,
          y: roundedE1RM,
          label: `${dateLabel}: ${roundedE1RM.toFixed(1)} kg e1RM`,
        });
      }
      series.volume.push({
        x: volIndex,
        y: Math.round(volume),
        label: `${dateLabel}: ${Math.round(volume)} kg total`,
      });
    }
  }
  // Keep the group when EITHER series has at least one point. Drops
  // exercises that never contributed any weighted set (pure-bw work
  // surfaces through PR records, not e1RM/volume charts).
  return Array.from(groups.values()).filter(
    (s) => s.e1rm.length >= 1 || s.volume.length >= 1,
  );
};

// Per-tag aggregate: total volume per session for each unique tag on
// the entry snapshot. Tags ride on `entry.tags` (stamped server-side at
// write time) so historical aggregates stay stable across catalog
// edits. Loaded-carry contributes weight only (distance lives on the
// duration field, not volume).
export type TagSeries = {
  tag: string;
  volume: Point[];
};

export const buildTagSeries = (
  workouts: Workout[],
  opts: BuildSeriesOptions = {},
): TagSeries[] => {
  const sorted = [...workouts].sort(
    (a, b) =>
      new Date(a.startedAt).getTime() - new Date(b.startedAt).getTime(),
  );
  const dateFmt =
    opts.dateFormatter ??
    new Intl.DateTimeFormat(undefined, { dateStyle: "medium" });
  // tag → date string → cumulative volume in that session
  const byTag = new Map<string, Map<string, number>>();
  for (const w of sorted) {
    const dateKey = w.startedAt;
    for (const entry of w.entries) {
      const tags = entry.tags ?? [];
      if (tags.length === 0) continue;
      let entryVolume = 0;
      for (const s of entry.sets) {
        // A fail counts like any set (AP-0p model): reps = clean done, fail
        // is a pure label — no exclusion.
        if (s.type === "weighted") {
          entryVolume += weightedVolumeKg(s.weight, s.reps, s.loadedSides);
        } else if (s.type === "loaded-carry") {
          entryVolume += s.weight;
        }
      }
      if (entryVolume <= 0) continue;
      for (const tag of tags) {
        let m = byTag.get(tag);
        if (!m) {
          m = new Map();
          byTag.set(tag, m);
        }
        m.set(dateKey, (m.get(dateKey) ?? 0) + entryVolume);
      }
    }
  }
  const series: TagSeries[] = [];
  for (const [tag, dateMap] of byTag) {
    const points: Point[] = [];
    let i = 0;
    for (const [iso, vol] of dateMap) {
      const dateLabel = dateFmt.format(new Date(iso));
      points.push({
        x: i++,
        y: Math.round(vol),
        label: `${dateLabel}: ${Math.round(vol)} kg`,
      });
    }
    series.push({ tag, volume: points });
  }
  series.sort((a, b) => a.tag.localeCompare(b.tag));
  return series;
};
